<template>
  <DatocmsStructuredText
    class="md:prose-md prose max-w-none lg:prose-lg"
    :data="data"
    :renderBlock="renderBlock"
    :renderInlineRecord="renderInlineRecord"
    :renderLinkToRecord="renderLinkToRecord"
  />
</template>

<script lang="ts">
import { StructuredText } from 'vue-datocms'

export default {
  components: {
    DatocmsStructuredText: StructuredText
  }
}
</script>

<script setup lang="ts">
const Button = resolveComponent('Button')
const HTML = resolveComponent('HTML')
const Image = resolveComponent('Image')
const ImageGallery = resolveComponent('ImageGallery')
const NuxtLink = resolveComponent('NuxtLink')
const Table = resolveComponent('Table')
const VideoEmbed = resolveComponent('VideoEmbed')
const Testimonials = resolveComponent('Testimonials')
const Tip = resolveComponent('Tip')
const MultipleImage = resolveComponent('MultipleImage')

const { localePathByType } = useLocalePathByType()

defineProps({
  data: {
    type: Object,
    default: () => {}
  }
})

function renderInlineRecord({ record }: { record: any }) {
  switch (record.__typename) {
    case 'PageRecord':
      return h(
        NuxtLink,
        { to: localePathByType(record.__typename, record.slug) },
        () => record.title
      )
    case 'ArticleRecord':
      return h(
        NuxtLink,
        {
          to: localePathByType(record.__typename, record.slug)
        },
        () => record.title
      )
    default:
      return null
  }
}

function renderLinkToRecord({ record, children, transformedMeta }: any) {
  switch (record.__typename) {
    case 'PageRecord':
      return h(
        NuxtLink,
        { ...transformedMeta, to: localePathByType(record.__typename, record.slug) },
        () => children
      )
    case 'ArticleRecord':
      return h(
        NuxtLink,
        {
          ...transformedMeta,
          to: localePathByType(record.__typename, record.slug)
        },
        () => children
      )
    default:
      return null
  }
}

function renderBlock({ record }: { record: any }) {
  switch (record.__typename) {
    case 'ButtonRecord':
      return h(Button, {
        label: record.label,
        variant: record.variant,
        icon: record.icon,
        to:
          record.reference !== null
            ? localePathByType(record.reference.__typename, record.reference.slug)
            : record.externalLink,
        target: record.externalLink && '_blank'
      })
    case 'HtmlRecord':
      return h(HTML, {
        data: record
      })
    case 'ImageRecord':
      return h(Image, {
        class: 'rounded-3xl object-cover',
        src: record.image.responsiveImage ? record.image.responsiveImage?.src : record.image.url,
        srcSet: record.image.responsiveImage ? record.image.responsiveImage?.webpSrcSet : undefined,
        width: record.image.responsiveImage
          ? record.image.responsiveImage?.width
          : record.image.width,
        height: record.image.responsiveImage
          ? record.image.responsiveImage?.height
          : record.image.height,
        alt: record.image.alt
      })
    case 'ImageGalleryRecord':
      return h(ImageGallery, {
        data: record.images,
        class: 'not-prose'
      })
    case 'VideoEmbedRecord':
      return h(VideoEmbed, {
        data: record.video,
        class:
          'my-12 lg:my-16 w-[calc(100%+40px)] lg:w-[calc(100%+224px)] aspect-video -ml-5 lg:-ml-28',
        elevated: true
      })
    case 'TableRecord':
      return h(Table, {
        data: record,
        class: '[&:not(:first-child)]:mt-16 [&:not(:first-child)]:md:mt-16]'
      })
    case 'TestimonialListRecord':
      return h(Testimonials, {
        class: 'my-8 lg:my-16',
        data: record.testimonials,
        elevated: true
      })
    case 'TipRecord':
      return h(Tip, {
        class: 'my-4 lg:my-8 not-prose',
        data: record
      })
    case 'MultipleImageRecord':
      return h(MultipleImage, {
        class: 'my-4 lg:my-8 not-prose',
        data: record
      })
    default:
      return null
  }
}
</script>
